import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Copy, Pencil, Trash2, Plus, Eye, UploadCloud, ImageIcon, X } from 'lucide-react';
import { Table, TableBody, TableHeader } from '../../components/ui/table';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomPagination from '../../components/common/pagination';
import DeleteModal from '../../components/deleteModal';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ImagePreviewModal, { PREVIEW_LAYOUTS } from '../../components/common/ImagePreview';
import { copyToClipboard } from '../../utils/copyToClipboard';
import { compressImage, ImageValidationError } from '../../utils/imageUpload';
import {
    getCardBgs,
    createCardBg,
    updateCardBg,
    deleteCardBg,
    deleteMultipleCardBgs,
} from '../../api/cardbgapi';
import CustomCheckbox from '../../components/common/CustomCheckbox';

const LIMIT = 15;
const MAX_FILES = 5;

// Card Preview column na buttons (1, 2, 5, 6, 7, 8, 9) - PREVIEW_LAYOUTS parthi automatic
const LAYOUT_KEYS = Object.keys(PREVIEW_LAYOUTS).map(Number);

const CardBg = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    // modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [editId, setEditId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // file state
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [previewUrls, setPreviewUrls] = useState([]);
    const [existingImageUrl, setExistingImageUrl] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    // preview state
    const [showPreview, setShowPreview] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [previewLayout, setPreviewLayout] = useState(null); // null | key of PREVIEW_LAYOUTS

    // delete state (single + multiple)
    const [deleteIds, setDeleteIds] = useState([]);
    const [deleting, setDeleting] = useState(false);

    // multi-select state
    const [selectedIds, setSelectedIds] = useState([]);

    const isEdit = modalMode === 'edit';
    const getId = (item) => item._id || item.id;

    // API response shape: { data, total, totalPages }  (same as Situation)
    const loadItems = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getCardBgs({ page: currentPage, limit: LIMIT });

            const pages = res?.totalPages || 1;

            // current page no longer exists (e.g. after delete) -> step back
            if (currentPage > pages) {
                setCurrentPage(pages);
                return;
            }

            setItems(res?.data || []);
            setTotalPages(pages);
            setTotalItems(res?.total || 0);
            setSelectedIds([]); // selection never survives a reload / page change
        } catch (error) {
            toast.error('Failed to load card backgrounds!');
        } finally {
            setLoading(false);
        }
    }, [currentPage]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    /* ---------------- selection ---------------- */

    const allSelected = items.length > 0 && items.every((item) => selectedIds.includes(getId(item)));

    const toggleSelectAll = () => {
        setSelectedIds(allSelected ? [] : items.map(getId));
    };

    const toggleSelectOne = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    /* ---------------- files ---------------- */

    const resetFiles = () => {
        previewUrls.forEach((url) => {
            if (url.startsWith('blob:')) URL.revokeObjectURL(url);
        });
        setSelectedFiles([]);
        setPreviewUrls([]);
        setExistingImageUrl('');
    };

    const processFiles = async (files) => {
        if (files.length === 0) return;

        if (isEdit) {
            if (files.length > 1) {
                toast.error('You can only upload one image when editing');
                return;
            }
        } else if (selectedFiles.length + files.length > MAX_FILES) {
            toast.error(`You can only upload a maximum of ${MAX_FILES} images`);
            return;
        }

        const processedFiles = [];
        const newPreviewUrls = [];

        for (const file of files) {
            try {
                // validates type, compresses if needed, and enforces the 5MB cap
                const processed = await compressImage(file);
                processedFiles.push(processed);
                newPreviewUrls.push(URL.createObjectURL(processed));
            } catch (error) {
                if (error instanceof ImageValidationError) {
                    toast.error(`${file.name}: ${error.message}`);
                } else {
                    toast.error(`Error processing ${file.name}`);
                }
            }
        }

        if (processedFiles.length === 0) return;

        if (isEdit) {
            // replace existing / previously picked file
            previewUrls.forEach((url) => {
                if (url.startsWith('blob:')) URL.revokeObjectURL(url);
            });
            setSelectedFiles(processedFiles);
            setPreviewUrls(newPreviewUrls);
        } else {
            setSelectedFiles((prev) => [...prev, ...processedFiles]);
            setPreviewUrls((prev) => [...prev, ...newPreviewUrls]);
        }
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = ''; // allow re-selecting the same file
        await processFiles(files);
    };

    const removeFile = (index) => {
        URL.revokeObjectURL(previewUrls[index]);
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
        setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (submitting) return;

        const imageFiles = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'));
        if (imageFiles.length === 0) {
            toast.error('Please upload image files only');
            return;
        }
        processFiles(imageFiles);
    };

    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!submitting) setIsDragging(true);
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const dropzoneDisabled = submitting || (!isEdit && selectedFiles.length >= MAX_FILES);

    /* ---------------- modal ---------------- */

    const openAddModal = () => {
        resetFiles();
        setModalMode('add');
        setEditId(null);
        setIsModalOpen(true);
    };

    const openEditModal = (item) => {
        resetFiles();
        setModalMode('edit');
        setEditId(getId(item));
        setExistingImageUrl(item.CardBg);
        setPreviewUrls([item.CardBg]);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        if (submitting) return;
        resetFiles();
        setIsModalOpen(false);
        setEditId(null);
    };

    /* ---------------- submit ---------------- */

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;

        try {
            setSubmitting(true);

            if (isEdit) {
                if (selectedFiles.length > 0) {
                    const res = await updateCardBg(editId, selectedFiles[0]);
                    toast.success(res?.message || 'Card background updated successfully!');
                } else if (existingImageUrl) {
                    toast.info('No changes made to the image');
                } else {
                    toast.error('Please select an image');
                    return;
                }
            } else {
                if (selectedFiles.length === 0) {
                    toast.error('Please select at least one image');
                    return;
                }
                const results = await Promise.all(selectedFiles.map((file) => createCardBg(file)));
                toast.success(`Successfully uploaded ${results.length} image(s)`);
            }

            resetFiles();
            setIsModalOpen(false);
            setEditId(null);

            if (!isEdit && currentPage !== 1) {
                setCurrentPage(1); // newest first -> go to page 1 (effect reloads)
            } else {
                loadItems();
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'An error occurred. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    /* ---------------- delete (single + multiple) ---------------- */

    const confirmDelete = (id) => setDeleteIds([id]);
    const confirmDeleteSelected = () => setDeleteIds(selectedIds);
    const cancelDelete = () => {
        if (!deleting) setDeleteIds([]);
    };

    const handleDelete = async () => {
        if (deleteIds.length === 0) return;

        try {
            setDeleting(true);

            if (deleteIds.length === 1) {
                const res = await deleteCardBg(deleteIds[0]);
                toast.success(res?.message || 'Card background deleted successfully!');
            } else {
                const res = await deleteMultipleCardBgs(deleteIds);
                toast.success(res?.message || `${deleteIds.length} card backgrounds deleted successfully!`);
            }

            // If the whole current page was deleted, step back one page
            const deletedWholePage = deleteIds.length >= items.length;
            setDeleteIds([]);

            if (deletedWholePage && currentPage > 1) {
                setCurrentPage((p) => p - 1); // effect reloads
            } else {
                loadItems();
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to delete!');
        } finally {
            setDeleting(false);
        }
    };

    /* ---------------- preview ---------------- */

    // layout: null -> normal image, otherwise key of PREVIEW_LAYOUTS (1, 2, 5, 6, 7, 8, 9)
    const handleShowPreview = (index, layout = null) => {
        setPreviewIndex(index);
        setPreviewLayout(layout);
        setShowPreview(true);
    };

    const handleClosePreview = () => {
        setShowPreview(false);
        setPreviewLayout(null);
    };

    const handlePreviewNavigation = (newIndex, direction) => {
        if (direction === 'next' && newIndex >= items.length) {
            if (currentPage < totalPages) {
                setPreviewIndex(0);
                setCurrentPage((p) => p + 1); // effect reloads
            }
        } else if (direction === 'prev' && newIndex < 0) {
            if (currentPage > 1) {
                setPreviewIndex(LIMIT - 1);
                setCurrentPage((p) => p - 1); // effect reloads
            }
        } else {
            setPreviewIndex(newIndex);
        }
    };

    /* ---------------- render ---------------- */

    const headerCellBase =
        'py-3 px-2 text-center font-medium text-gray-500 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700';
    const bodyCellBase =
        'py-3 px-3 text-center align-middle text-gray-700 dark:text-gray-300 border-r border-b border-gray-200 dark:border-gray-700';
    const layoutBtn =
        'h-8 w-8 rounded-lg border border-brand-500 text-sm font-semibold text-brand-500 transition-colors hover:bg-brand-500 hover:text-white';
    const colCount = 5; // checkbox + index + image + card preview + actions

    return (
        <div className="flex flex-col h-[calc(100vh-120px)] min-h-0">
            {/* Header: breadcrumb left, actions right */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <PageBreadcrumb pageTitle="Annoy Funny CardBg" />

                <div className="flex items-center gap-3 mb-6">
                    <button
                        onClick={openAddModal}
                        className="inline-flex items-center gap-2 justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white"
                    >
                        <Plus size={16} /> Add CardBg
                    </button>
                </div>
            </div>

            <div className="flex-1 min-h-0 overflow-hidden">
                <div className="flex h-full min-h-0 flex-col rounded-2xl border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
                    <div className="flex shrink-0 items-center justify-between">


                        {selectedIds.length > 0 && (
                            <button
                                onClick={confirmDeleteSelected}
                                className="inline-flex items-center gap-2 justify-center rounded-lg bg-red-500 px-4 py-2 my-3 mx-3 text-sm font-medium text-white hover:bg-red-600"
                            >
                                <Trash2 size={16} /> Delete Selected ({selectedIds.length})
                            </button>
                        )}
                    </div>

                    <div className="flex-1 min-h-0 overflow-hidden p-4 border-t border-gray-100 dark:border-gray-800 sm:p-6 pt-4">
                        <div className="h-full min-h-0 rounded-lg border dark:border-gray-800 overflow-auto">
                            <Table className="w-full">
                                <TableHeader className="sticky top-0 z-30 border-b border-gray-200 bg-white dark:border-white/[0.05] dark:bg-gray-900">
                                    <tr>
                                        <th className={`${headerCellBase} w-12 bg-white dark:bg-gray-900`}>
                                            <CustomCheckbox
                                                checked={allSelected}
                                                onChange={toggleSelectAll}
                                                disabled={items.length === 0}
                                            />
                                        </th>
                                        <th className={`${headerCellBase} bg-white dark:bg-gray-900`}>Index</th>
                                        <th className={`${headerCellBase} bg-white dark:bg-gray-900`}>Annoy Funny CardBg</th>
                                        <th className={`${headerCellBase} bg-white dark:bg-gray-900`}>Card Preview</th>
                                        <th className="py-3 px-2 text-center font-medium text-gray-500 bg-white dark:text-gray-400 dark:bg-gray-900">
                                            Actions
                                        </th>
                                    </tr>
                                </TableHeader>

                                <TableBody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={colCount} className="py-6 px-2 text-center text-gray-500 dark:text-gray-400">
                                                Loading...
                                            </td>
                                        </tr>
                                    ) : items.length === 0 ? (
                                        <tr>
                                            <td colSpan={colCount} className="py-6 px-2 text-center text-gray-500 dark:text-gray-400">
                                                No data found.
                                            </td>
                                        </tr>
                                    ) : (
                                        items.map((item, index) => {
                                            const id = getId(item);
                                            return (
                                                <tr key={id} className="hover:bg-gray-50/60 dark:hover:bg-white/[0.02]">
                                                    <td className={bodyCellBase}>
                                                        <CustomCheckbox
                                                            checked={selectedIds.includes(id)}
                                                            onChange={() => toggleSelectOne(id)}
                                                        />
                                                    </td>

                                                    <td className={bodyCellBase}>
                                                        {(currentPage - 1) * LIMIT + index + 1}
                                                    </td>

                                                    <td className={bodyCellBase}>
                                                        <div className="group relative mx-auto h-[100px] w-[70px] overflow-hidden rounded-md">
                                                            {/* placeholder shown until image loads */}
                                                            <div className="absolute inset-0 flex items-center justify-center bg-gray-200 animate-pulse dark:bg-gray-700">
                                                                <ImageIcon size={22} className="text-gray-400" />
                                                            </div>

                                                            <img
                                                                src={item.CardBg}
                                                                alt="CardBg thumbnail"
                                                                className="relative z-10 h-full w-full object-cover"
                                                                style={{ opacity: 0 }}
                                                                onLoad={(e) => {
                                                                    e.target.style.opacity = 1;
                                                                    e.target.previousSibling.style.display = 'none';
                                                                }}
                                                            />

                                                            <div className="absolute inset-0 z-20 flex items-center justify-center gap-2 bg-black/60 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleShowPreview(index)}
                                                                    title="Preview"
                                                                    className="text-white hover:text-brand-500 transition-colors"
                                                                >
                                                                    <Eye size={16} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => copyToClipboard(item.CardBg, 'Image URL')}
                                                                    title="Copy URL"
                                                                    className="text-white hover:text-brand-500 transition-colors"
                                                                >
                                                                    <Copy size={16} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </td>

                                                    {/* Card Preview column: PREVIEW_LAYOUTS ni badhi layouts (1, 2, 5-9) */}
                                                    <td className={bodyCellBase}>
                                                        <div className="mx-auto flex max-w-[220px] flex-wrap items-center justify-center gap-2">
                                                            {LAYOUT_KEYS.map((key) => (
                                                                <button
                                                                    key={key}
                                                                    type="button"
                                                                    onClick={() => handleShowPreview(index, key)}
                                                                    title={`Card Preview ${key}`}
                                                                    className={layoutBtn}
                                                                >
                                                                    {key}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </td>

                                                    <td className="py-3 px-2 text-center align-middle border-b border-gray-200 dark:border-gray-700">
                                                        <div className="flex items-center justify-center gap-3">
                                                            <button onClick={() => openEditModal(item)} title="Edit" className="text-brand-600">
                                                                <Pencil size={16} />
                                                            </button>
                                                            <button
                                                                onClick={() => confirmDelete(id)}
                                                                title="Delete"
                                                                className="text-red-500 hover:text-red-600"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                </div>
            </div>

            <div className="shrink-0 w-full border-t dark:bg-gray-900 dark:border-gray-700 mt-0">
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={LIMIT}
                    totalItems={totalItems}
                />
            </div>

            {/* Add/Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-99999 flex items-center justify-center bg-black/50 px-4">
                    <div
                        className="relative w-full max-w-lg max-h-[95vh] overflow-y-auto rounded-xl bg-white p-6 dark:bg-gray-900"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                                {isEdit ? 'Edit Card Background' : 'Add Card Background'}
                            </h3>
                            <button
                                type="button"
                                onClick={closeModal}
                                disabled={submitting}
                                title="Close"
                                className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-60 dark:text-gray-400 dark:hover:bg-white/[0.05] dark:hover:text-gray-200"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                {isEdit ? 'Select new image to replace the existing one' : 'Card Background Images'}{' '}
                                <span className="text-xs text-gray-400">(Max {MAX_FILES} images, 5 MB each)</span>
                                <span className="pl-1 text-red-500">*</span>
                            </label>

                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                disabled={dropzoneDisabled}
                                className="hidden"
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                multiple={!isEdit}
                            />

                            <div
                                onClick={() => !dropzoneDisabled && fileInputRef.current?.click()}
                                onDragOver={onDragOver}
                                onDragLeave={onDragLeave}
                                onDrop={onDrop}
                                className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center transition-colors
                        ${isDragging
                                        ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                                        : 'border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-white/[0.03]'}
                        ${dropzoneDisabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:border-brand-500'}`}
                            >
                                <UploadCloud size={28} className={isDragging ? 'text-brand-500' : 'text-gray-400'} />
                                {isDragging ? (
                                    <span className="mt-2 font-medium text-brand-500">Drop images here</span>
                                ) : (
                                    <>
                                        <span className="mt-2 text-sm text-gray-500 dark:text-gray-400">Drag & drop images or</span>
                                        <span className="font-medium text-brand-500">Browse files</span>
                                        {!isEdit && (
                                            <span className="mt-1 text-xs text-gray-400">
                                                {selectedFiles.length}/{MAX_FILES} images selected
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>

                            {previewUrls.length > 0 && (
                                <div className="mt-4 grid grid-cols-3 gap-3">
                                    {previewUrls.map((url, index) => (
                                        <div key={url} className="relative">
                                            <div className="flex h-32 justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-white/[0.03]">
                                                <img src={url} alt={`Preview ${index + 1}`} className="h-full w-full object-contain" />
                                            </div>
                                            {selectedFiles.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeFile(index)}
                                                    disabled={submitting}
                                                    className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600"
                                                >
                                                    ×
                                                </button>
                                            )}
                                            <div className="mt-1 truncate text-center text-xs text-gray-500 dark:text-gray-400">
                                                {selectedFiles[index]?.name || 'Existing image'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    disabled={submitting}
                                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 dark:border-gray-700 dark:text-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                                >
                                    {submitting ? 'Saving...' : isEdit ? 'Update' : 'Add'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <DeleteModal
                isOpen={deleteIds.length > 0}
                value={deleteIds.length > 1 ? `${deleteIds.length} card backgrounds` : 'card background'}
                isDeleting={deleting}
                onClose={cancelDelete}
                onConfirm={handleDelete}
            />

            {showPreview && (
                <ImagePreviewModal
                    show={showPreview}
                    onHide={handleClosePreview}
                    images={items.map((item) => item.CardBg)}
                    currentIndex={previewIndex % LIMIT}
                    onNavigate={handlePreviewNavigation}
                    totalImages={totalItems}
                    currentPage={currentPage}
                    itemsPerPage={LIMIT}
                    layout={previewLayout}
                />
            )}

            <ToastContainer position="top-center" className="!z-[99999]" />
        </div>
    );
};

export default CardBg;